import React from "react";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  EllipsisVerticalIcon,
  PlusIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon,
  ShoppingBagIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { downloadAllInvoicePDF, getAllPayments, uploadTaxInvoice } from "@/store/slices/orderSlice";
import { EyeIcon, XCircleIcon, ArrowUpTrayIcon } from "@heroicons/react/24/solid";
import DownloadFile from "@/components/DownloadFile";
import DocumentViewModal from "@/components/DocumentViewModal";
import Select from 'react-select';
import { Button, IconButton, Input } from "@material-tailwind/react";
import { Link } from "react-router-dom";
import { handleDownloadURL } from "@/components/DownloadFile";
import Toaster from "@/components/Toaster";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import { approveRejectPaymentReceipt, initiatePaymentByAdmin } from "@/store/slices/rfqSlice";
import { Popover } from "react-tiny-popover";
import Swal from "sweetalert2";
import moment from "moment";
import { useTranslation } from "react-i18next";


export default function PaymentsPage() {
  const { t } = useTranslation("payment");
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [documentUrl, setDocumentUrl] = useState("");
  const [selected, setSelected] = useState([]);
  const [actionPopoverOpen, setActionPopoverOpen] = useState({});

  const { user } = useSelector((state) => state.user);

  const { payment_list,payment_card_data, hasMore, list_loading, loading } = useSelector((state) => state.order);

  const { loading:rfqLoading } = useSelector((state) => state.rfq);
  
  // Infinite Scroll Observer
  const observer = useRef();
  const lastElementRef = useCallback(
    (node) => {
      if (loading) return; // Stop if a request is already in progress

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        // Check entries[0].isIntersecting AND hasMore
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore] // Critical dependencies
  );

  useEffect(() => {
    dispatch(getAllPayments({ page:page, search:search , filter:filter}));
  }, [dispatch, page]);

  const toggleRow = (id) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handelSearch = (event) => {
      const value = event.target.value;
      if (event.key === "Enter" && value) {
          dispatch(getAllPayments({ page:page, search:value , filter:filter}));
          setSelected([]);
      } else if (event.key === "Backspace" && value.length === 0) {
          dispatch(getAllPayments({ page:page, search:'' , filter:filter}));
          setSelected([]);
      }
  }

  const handleSearchClick = () => {
      dispatch(getAllPayments({ page:page, search:search , filter:filter}));
      setSelected([]);
  }

  const handelRemoveSearch = () => {
      setSearch('');
      dispatch(getAllPayments({ page:page, search:'' , filter:filter}));
  }

  const handelFilter = (value) => {
      setFilter(value);
      dispatch(getAllPayments({ page:page, search:search , filter:value}));
      setSelected([]);
  }

  const toggleSelectAll = () => {
    if (selected.length === payment_list?.paymentlist?.data?.length) {
      setSelected([]);
    } else {
      setSelected(payment_list?.paymentlist?.data?.map((i) => i.rfq_id));
    }
  };

  const toggleSelect = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((i) => i !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const downloadAllInvoice = () => {
    if(selected?.length == 0)return;
    dispatch(downloadAllInvoicePDF({ payment_ids: selected })).unwrap().then((res) => {
        if (res?.status) {
            Toaster.success(t("invoicesDownloadedSuccess"));
            handleDownloadURL({ fileUrl: res?.data?.pdf_url });
            setSelected([]);
        } else {
            Toaster.error(t("somethingWentWrong"));
        }
    }).catch((error) => {
        Toaster.error(t("failedToDownload"));
    });
  }

  const toggleActionPopover = (id) => {
      setActionPopoverOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const onApproveReject = (type, id, summary) => {
    const msg = type === "approved" ? "approve" : "reject";
    
    if (type === 'approved') {
        Swal.fire({
            title: t("approvePaymentReceipt"),
            icon: "question",
            html: `
                <div class="text-left mt-4">
                    <div class="mb-4">
                        <label for="swal-amount" class="block text-sm font-medium text-gray-700 mb-1">
                            ${t("receiptAmount")}
                        </label>
                        <input 
                            type="number" 
                            id="swal-amount" 
                            class="swal2-input !m-0 !w-full !border !border-gray-300 focus:!border-green-500 !rounded-md !shadow-sm" 
                            placeholder="0.00" 
                            step="0.01"
                        >
                    </div>
                    
                    <div class="mb-2">
                        <label for="swal-payment-type" class="block text-sm font-medium text-gray-700 mb-1">
                            ${t("paymentType")}
                        </label>
                        <select 
                            id="swal-payment-type" 
                            class="swal2-select !m-0 !w-full !flex !border !border-gray-300 focus:!border-green-500 !rounded-md !shadow-sm !h-[42px] !px-2"
                        >
                            <option value="" disabled selected>${t("selectPaymentType")}</option>
                            <option value="1">${t("cash")}</option>
                            <option value="2">${t("netBanking")}</option>
                            <option value="3">${t("upi")}</option>
                            <option value="4">${t("rtgs")}</option>
                            <option value="5">${t("cheque")}</option>
                            <option value="6">${t("others")}</option>
                        </select>
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: t("approveAndSave"),
            cancelButtonText: t("cancel", { ns: "common" }),
            confirmButtonColor: "#16a34a",
            cancelButtonColor: "#d33",
            focusConfirm: false,
            preConfirm: () => {
                const amount = document.getElementById('swal-amount').value;
                const paymentType = document.getElementById('swal-payment-type').value;

                const numericAmount = Number(amount);
                const pendingAmount = Number(summary?.amount_pending ?? 0);

                if (!amount) {
                    Swal.showValidationMessage(t("receiptAmountRequired"));
                    return false;
                }

                if (numericAmount <= 0) {
                    Swal.showValidationMessage(t("amountGreaterThanZero"));
                    return false;
                }

                if (numericAmount > pendingAmount + 0.001) { 
                    Swal.showValidationMessage(
                        t("amountExceedsPending", { pendingAmount: pendingAmount })
                    );
                    return false;
                }

                if (!paymentType) {
                    Swal.showValidationMessage(t("paymentTypeRequired"));
                    return false;
                }

                return { 
                    amount_paid: numericAmount, 
                    payment_mode: paymentType 
                };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const { amount_paid, payment_mode } = result.value;
                
                dispatch(approveRejectPaymentReceipt({ 
                    status: type, 
                    id: id, 
                    amount_paid, 
                    payment_mode 
                }))
                .unwrap()
                .then(() => {
                    Toaster.success(t("receiptApprovedSuccess"));
                     dispatch(getAllPayments({ page:page, search:search , filter:filter}));
                })
                .catch(() => Toaster.error(t("failedToPerformAction")));
            }
        });
    } else {
        Swal.fire({
            title: t("rejectPaymentReceipt"),
            icon: "warning",
            input: "textarea", 
            inputPlaceholder: t("rejectionReasonPlaceholder"),
            inputAttributes: {
                autocapitalize: "off"
            },
            showCancelButton: true,
            confirmButtonColor: "#16a34a",
            cancelButtonColor: "#dc2626",
            confirmButtonText: t("submitRejection"),
            inputValidator: (value) => {
                if (!value) {
                    return t("rejectionReasonRequired");
                }
                if (value.length < 10) {
                    return t("rejectionReasonMinLength");
                }
                if (value.length > 100) {
                    return t("rejectionReasonMaxLength");
                }
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const reason = result.value;

                dispatch(approveRejectPaymentReceipt({ 
                    status: type, 
                    id: id, 
                    reason: reason 
                }))
                .unwrap()
                .then(() => {
                    Toaster.success(t("receiptRejectedSuccess"));
                    dispatch(getAllPayments({ page:page, search:search , filter:filter}));
                })
                .catch(() => Toaster.error(t("failedToReject")));
            }
        });
    }
};

useEffect(() => {
  if (documentUrl) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "auto";
  }

  return () => {
    document.body.style.overflow = "auto"; // cleanup
  };
}, [documentUrl]);

  const initiatePayment = (rfq_id, item) => {
      Swal.fire({
          title: t("initiatePayment"),
          icon: "question",
          html: `
              <div class="text-left mt-4">

                  <div class="mb-4">
                      <label class="block text-sm font-medium text-gray-700 mb-1">
                          ${t("amount")}
                      </label>
                      <input 
                          type="number" 
                          id="swal-amount" 
                          class="swal2-input !m-0 !w-full !border !border-gray-300 focus:!border-green-500 !rounded-md !shadow-sm" 
                          placeholder="0.00" 
                          step="0.01"
                      >
                  </div>

                  <div class="mb-4">
                      <label class="block text-sm font-medium text-gray-700 mb-1">
                          ${t("paymentType")}
                      </label>
                      <select 
                          id="swal-payment-type" 
                          class="swal2-select !m-0 !w-full !border !border-gray-300 focus:!border-green-500 !rounded-md !shadow-sm !h-[42px] !px-2"
                      >
                          <option value="" disabled selected>${t("selectPaymentType")}</option>
                          <option value="1">${t("cash")}</option>
                          <option value="2">${t("netBanking")}</option>
                          <option value="3">${t("upi")}</option>
                          <option value="4">${t("rtgs")}</option>
                          <option value="5">${t("cheque")}</option>
                          <option value="6">${t("others")}</option>
                      </select>
                  </div>

                  <div class="mb-2">
                      <label class="block text-sm font-medium text-gray-700 mb-1">
                          ${t("uploadReceipt")}
                      </label>
                      <input 
                          type="file" 
                          id="swal-file" 
                          accept=".pdf,image/*"
                          class="swal2-file !m-0 !w-full"
                      >
                      <p class="text-xs text-gray-500 mt-1">
                          ${t("allowedFileTypes")}
                      </p>
                  </div>

              </div>
          `,
          showCancelButton: true,
          confirmButtonText: t("submitPayment"),
          cancelButtonText: t("cancel", { ns: "common" }),
          confirmButtonColor: "#16a34a",
          cancelButtonColor: "#d33",
          focusConfirm: false,

          preConfirm: () => {
              const amount = document.getElementById("swal-amount").value;
              const paymentType = document.getElementById("swal-payment-type").value;
              const fileInput = document.getElementById("swal-file");
              const file = fileInput.files[0];

              // Amount validation
              if (!amount) {
                  Swal.showValidationMessage(t("amountRequired"));
                  return false;
              }
              if (Number(amount) <= 0) {
                  Swal.showValidationMessage(t("amountGreaterThanZero"));
                  return false;
              }

              if (Number(amount) > Number(item?.amount_pending) + 0.001) { // Allow for floating point inaccuracies
                  Swal.showValidationMessage(t("amountExceedsPendingInitiate", { pendingAmount: item?.amount_pending }));
                  return false;
              }

              // Payment type validation
              if (!paymentType) {
                  Swal.showValidationMessage(t("paymentTypeRequired"));
                  return false;
              }

              // File validation
              if (!file) {
                  Swal.showValidationMessage(t("receiptRequired"));
                  return false;
              }

              const allowedTypes = [
                  "application/pdf",
                  "image/jpeg",
                  "image/png",
                  "image/jpg"
              ];

              if (!allowedTypes.includes(file.type)) {
                  Swal.showValidationMessage(t("fileTypeNotAllowed"));
                  return false;
              }

              return {
                  amount_paid: Number(amount),
                  payment_mode: paymentType,
                  payment_receipt: file
              };
          }

      }).then((result) => {
          if (result.isConfirmed) {
              const { amount_paid, payment_mode, payment_receipt } = result.value;

              // IMPORTANT: use FormData for file upload
              const formData = new FormData();
              formData.append("amount_paid", amount_paid);
              formData.append("payment_mode", payment_mode);
              formData.append("payment_receipt[]", payment_receipt);

              dispatch(initiatePaymentByAdmin({rfq_id, formData}))
                  .unwrap()
                  .then((res) => {
                      if(res?.status){
                          Toaster.success(t("paymentSuccess"));
                          dispatch(getAllPayments({ page:page, search:search , filter:filter}));
                      }else{
                          Toaster.error(res?.message || t("somethingWentWrong"))
                      }
                  })
                  .catch(() => Toaster.error(t("failedToPerformAction")));
          }
      });
    };

  const handleTaxInvoiceUpload = (e, paymentDetailId) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      Toaster.error(t("fileTypeNotAllowed"));
      return;
    }

    dispatch(uploadTaxInvoice({ payment_detail_id: paymentDetailId, uploaded_tax_invoice: file }))
      .unwrap()
      .then((res) => {
        Toaster.success(t("taxInvoiceUploadSuccess"));
        dispatch(getAllPayments({ page: page, search: search, filter: filter }));
      })
      .catch(() => Toaster.error(t("taxInvoiceUploadFailed")));
  };
    
  const action_options = [
    {
      label: t("all"),
      value: "all",
    },
    {
      label: t("pending"),
      value: "pending",
    },
    {
      label: t("partial"),
      value: "partial",
    },
    {
      label: t("paid"),
      value: "paid",
    },
  ];
  return (
    <div className="p-6 min-h-screen">
      {/* Header */}
       {(loading || rfqLoading) && <ShowLoader />}
        <ScrollToTop />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t("title")}</h1>

        <div className="w-full md:flex-1 flex justify-end">
            <div className="w-full max-w-md relative">
                <Input className={search ? 'pr-10' : ''} label={t("searchPlaceholder")} icon={!search ? <MagnifyingGlassIcon className="h-5 w-5" /> : ''} value={search} onChange={(e) => setSearch(e.target.value)} onKeyUp={handelSearch} />
                {search && (
                    <IconButton variant="text" size="sm" className="!absolute right-1 top-1" onClick={handelRemoveSearch}>
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                    </IconButton>
                )}
            </div>
            {search && (<Button variant="outlined" className="flex items-center border-gray-300 capitalize font-bold bg-black text-white mx-2"
                onClick={handleSearchClick}
            ><MagnifyingGlassIcon className="w-4 h-4 mr-1" /> {t("searchButton")}</Button>)}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard title={t("totalAmount")} value={payment_card_data?.total_amount || 0} />
        <StatCard title={t("amountPending")} value={payment_card_data?.amount_pending || 0} />
        <StatCard title={t("thisMonth")} value={payment_card_data?.this_month || 0} />
        <StatCard title={t("refundRequest")} value={payment_card_data?.refund_amount || 0} />
      </div>

      {/* Toolbar */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{t("invoices")}</span>
        </div>

        <div className="flex gap-3">
          <Select
            placeholder={t("selectFilter")}
            onChange={(row) => handelFilter(row.value)}
            options={action_options}
            value={action_options?.find(opt => opt.value === filter) || null}
          />
          {selected.length > 0 && (
          <button className="bg-black text-white px-4 py-2 rounded-lg" onClick={() => downloadAllInvoice()}>
            {t("download")} ({selected.length})
          </button>
          )}
        </div>
      </div>

        {/* Table */}
        <div className="overflow-hidden">
        <div className="overflow-x-auto">

          <table className="min-w-full text-sm text-left">
          <thead className="border-b">
            <tr className="text-gray-600 uppercase text-xs tracking-wider">
              <th className="px-4 py-3">
                <input type="checkbox" className="w-4 h-4 accent-green cursor-pointer"
                checked={
                selected.length > 0 &&
                selected.length === payment_list?.paymentlist?.data?.length
                }
                onChange={toggleSelectAll}
              />
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t("orderDetails")}</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t("customer")}</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t("totalAmountHeader")}</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t("items")}</th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t("status")}</th>
              <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">{t("action")}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
           {list_loading && (
            <tr>
              <td colSpan={7}>
                <div className="flex items-center justify-center py-4">
                  <span className="flex space-x-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce [animation-delay:0.15s]"></span>
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce [animation-delay:0.3s]"></span>
                  </span>
                </div>
              </td>
            </tr>
          )}
            {payment_list?.paymentlist?.data?.map((item, index) => {
              const isExpanded = expandedRows.includes(item.quotation_id);
              const isLastElement = payment_list.paymentlist?.data?.length === index + 1;
              const totalAdjustment = item.summary?.adjustments?.length > 0 ? item.summary?.adjustments?.reduce(
                (sum, item) => sum + Number(item.adjustment_amount || 0),
                0
              ) : null;

              return (
                <React.Fragment key={item.quotation_id}>
                  {/* Primary Row (Order & Customer) */}
                  <tr 
                    ref={isLastElement ? lastElementRef : null}
                    className={`hover:bg-blue-50/30 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-50/50' : ''}`}
                    onClick={() => toggleRow(item.quotation_id)}
                  >
                    <td className="px-4 py-4">
                      <input type="checkbox" className="w-4 h-4 accent-green cursor-pointer"
                      checked={selected.includes(item.rfq_id)}
                      onChange={() => toggleSelect(item.rfq_id)}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {/* <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                          <ShoppingBagIcon className="w-5 h-5" />
                        </div> */}
                        <div>
                        <Link to={`/dashboard/orders/${item.order?.id}`}
                        className="font-bold text-sm text-[#1A1918] hover:text-blue-600 hover:underline cursor-pointer transition">
                          {item.order?.order_id}
                        </Link>
                          <p className="text-xs text-gray-500">{t("po")}: {item.order?.purchase_order_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {/* Tooltip Wrapper */}
                        <div className="relative group cursor-pointer">
                          {/* Profile Image / Fallback */}
                          <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center">
                            <Popover
                              isOpen={actionPopoverOpen[`customer_${item.quotation_id}`]}
                              positions={["top", "right"]}
                              padding={8}
                              onClickOutside={() =>
                                setActionPopoverOpen((prev) => ({
                                  ...prev,
                                  [`customer_${item.quotation_id}`]: false,
                                }))
                              }
                              content={
                                <div className="bg-white border border-gray-200 shadow-xl rounded-xl p-4 text-sm w-64 z-[9999]">
                                  <h4 className="font-bold text-gray-900 border-b pb-2 mb-2">
                                    {t("customerDetails")}
                                  </h4>

                                  <div className="space-y-2 text-gray-600">
                                    <p className="flex justify-between">
                                      <span className="font-medium text-gray-400">{t("email")}:</span>
                                      <span className="text-right truncate ml-2">
                                        {item.payments[0]?.user?.email}
                                      </span>
                                    </p>

                                    <p className="flex justify-between">
                                      <span className="font-medium text-gray-400">{t("mobile")}:</span>
                                      <span>
                                        {item.payments[0]?.user?.country_code || ''}{" "}
                                        {item.payments[0]?.user?.mobile_number || 'N/A'}
                                      </span>
                                    </p>

                                    <p className="flex justify-between">
                                      <span className="font-medium text-gray-400">{t("altMobile")}:</span>
                                      <span>
                                        {item.payments[0]?.user?.alternate_country_code || ''}{" "}
                                        {item.payments[0]?.user?.alternate_mobile || 'N/A'}
                                      </span>
                                    </p>

                                    <p className="flex justify-between">
                                      <span className="font-medium text-gray-400">{t("company")}:</span>
                                      <span>
                                        {item.payments[0]?.user?.company_name}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              }
                            >
                              <div
                                className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center cursor-pointer"
                                onMouseEnter={() =>
                                  setActionPopoverOpen((prev) => ({
                                    ...prev,
                                    [`customer_${item.quotation_id}`]: true,
                                  }))
                                }
                                onMouseLeave={() =>
                                  setActionPopoverOpen((prev) => ({
                                    ...prev,
                                    [`customer_${item.quotation_id}`]: false,
                                  }))
                                }
                              >
                                {item.payments[0]?.user?.profile_image ? (
                                  <img
                                    src={item.payments[0]?.user?.profile_image}
                                    alt="User"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <UserIcon className="w-6 h-6 text-gray-400" />
                                )}
                              </div>
                            </Popover>
                          </div>
                        </div>

                        {/* Visible Name/Company Labels */}
                        <div>
                       <p className="text-xs text-gray-900 font-bold">
                            {item.payments[0]?.user?.first_name} {item.payments[0]?.user?.last_name}
                        </p>
                          <p className="text-xs text-gray-500 font-medium">{item.payments[0]?.user?.company_name}</p>
                        </div>
                      </div>
                    </td>
                     <td className="px-6 py-4 text-sm text-gray-600">
                     SAR {item.summary?.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {item.order?.total_items} {t("items")}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={item?.order?.payment_status || ''} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      {isExpanded ? <ChevronUpIcon className="w-5 h-5 inline text-gray-400" /> : <ChevronDownIcon className="w-5 h-5 inline text-gray-400" />}
                    </td>
                  </tr>

                  {/* Collapsible Payment History */}
                  {isExpanded && (
                    <tr>
                      <td colSpan="7" className="py-2 bg-gray-50/50">
                        <div className="overflow-hidden">
                          <div className="overflow-hidden border border-gray-200 rounded-xl bg-white shadow-sm mx-4 mb-2">
                            {/* Financial Summary Header */}
                            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                              <div className={`grid gap-4 ${(totalAdjustment || item?.order?.order_status === 'cancelled') ? 'grid-cols-5' : 'grid-cols-4'}`}>
                                <div className="border-r border-gray-200 pr-4">
                                  <p className="text-[10px] text-gray-900 uppercase font-bold tracking-wider">{t("totalAmountHeader")}</p>
                                  <p className="text-sm font-black text-gray-900">
                                    SAR {item.summary?.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}
                                  </p>
                                </div>
                                <div className="border-r border-gray-200 px-4">
                                  <p className="text-[10px] text-green-500 uppercase font-bold tracking-wider">{t("amountPaid")}</p>
                                  <p className="text-sm font-black text-green-700">
                                    SAR {item.summary?.amount_paid?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}
                                  </p>
                                </div>
                                <div className="border-r border-gray-200 px-4">
                                  <p className="text-[10px] text-orange-500 uppercase font-bold tracking-wider">{t("amountPending")}</p>
                                  <p className="text-sm font-black text-orange-700">
                                    SAR {item.summary?.amount_pending?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}
                                  </p>
                                </div>
                                <div className="border-r border-gray-200 pl-4">
                                  <p className="text-[10px] text-red-400 uppercase font-bold tracking-wider">{t("refundAmount")}</p>
                                  <p className="text-sm font-black text-red-600">
                                    SAR {item.summary?.refund_request?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}
                                  </p>
                                </div>
                               {totalAdjustment &&  
                                <div className="px-4">
                                  <div className="text-[10px] text-green-500 uppercase font-bold tracking-wider flex items-center gap-2">
                                    <span>{item?.order?.order_status === 'cancelled' ? t("adjustTo") : t("adjustFrom")}</span>
                                   <Popover
                                      isOpen={actionPopoverOpen[`adjust_${item.quotation_id}`]}
                                      positions={["top", "left", "right"]}
                                      padding={8}
                                      onClickOutside={() =>
                                        setActionPopoverOpen((prev) => ({
                                          ...prev,
                                          [`adjust_${item.quotation_id}`]: false,
                                        }))
                                      }
                                      content={
                                        <div className="bg-white border border-gray-200 shadow-xl rounded-xl p-4 text-sm w-64 z-[9999] w-[420px] max-w-[90vw]">
                                        <div className="flex items-center justify-between border-b pb-2 mb-2">
                                          <h4 className="font-bold text-gray-900">
                                            {t("amountAdjustInOrders")}
                                          </h4>
                                            {item?.order?.order_status === 'cancelled' &&
                                              <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold bg-red-100 text-red-700`}>
                                                {t("cancelled")}
                                              </span>
                                            }

                                        </div>

                                        <div className="space-y-3 text-gray-700">
                                          {item.summary?.adjustments?.length > 0 ? (
                                            item.summary.adjustments.map((row, index) => (
                                              <div
                                                key={index}
                                                className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                                              >
                                                {/* Left: From Order */}
                                                <div className="flex flex-col text-xs">
                                                  <span className="text-gray-400">{t("from")}</span>
                                                  <span className="font-semibold text-gray-800">
                                                    {row.from_order?.order_id || '-'}
                                                  </span>
                                                </div>

                                                {/* Center: Arrow + Amount */}
                                                <div className="flex flex-col items-center px-2">
                                                  <span className="text-xs text-gray-400">→</span>
                                                  <span className="text-sm font-bold text-green-600">
                                                    SAR {Number(row.adjustment_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                  </span>
                                                </div>

                                                {/* Right: To Order */}
                                                <div className="flex flex-col text-xs text-right">
                                                  <span className="text-gray-400">{t("to")}</span>
                                                  <span className="font-semibold text-gray-800">
                                                    {row.to_order?.order_id || '-'}
                                                  </span>
                                                </div>
                                              </div>
                                            ))
                                          ) : (
                                            <p className="text-gray-400 text-sm">{t("noAdjustments")}</p>
                                          )}
                                        </div>
                                        </div>
                                      }
                                    >
                                      <EyeIcon
                                        className="h-4 w-4 cursor-pointer"
                                        onClick={() =>
                                          setActionPopoverOpen((prev) => ({
                                            ...prev,
                                            [`adjust_${item.quotation_id}`]: !prev[`adjust_${item.quotation_id}`],
                                          }))
                                        }
                                      />
                                    </Popover>
                                  </div>

                                  <p className="text-sm font-black text-green-700">
                                    SAR {totalAdjustment?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}
                                  </p>
                                </div>}
                              </div>
                            </div>
                            </div>
                            <div className="overflow-hidden border border-gray-200 rounded-xl bg-white shadow-sm mx-4 mb-4">
                            {/* Sub-header for the table records */}
                            <div className="px-6 py-2 bg-white border-b border-gray-100 flex justify-between items-center">
                              <div className="flex gap-3">
                                 <div className="flex flex-col">

                                    <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest transition-all">
                                      {t("transactionHistory")}
                                    </span>
                                    {(item.payments?.length === 0 && item.summary?.credit_date) && (() => {
                                      const creditDate = item.summary?.credit_date;
          
                                      if (!creditDate) return null;
          
                                      const now = moment();
                                      const dueDate = moment(creditDate);
          
                                      let status = "";
                                      let colorClass = "";
          
                                      if (now.isAfter(dueDate)) {
                                          status = t("delayed");
                                          colorClass = "text-red-500";
                                      } else if (now.isSame(dueDate, "day")) {
                                          status = t("dueToday");
                                          colorClass = "text-yellow-500";
                                      } else {
                                          status = t("onTime");
                                          colorClass = "text-green-600";
                                      }
          
                                      return (
                                          <p className={`text-sm font-medium ${colorClass}`}>
                                              {t("creditDate")}: {dueDate.format("D MMM YYYY")} | {status}
                                          </p>
                                      );
                                  })()}
                                  {(item.payments?.length > 0 && item.summary?.credit_date) && 
                                      <p className="text-[12px] text-gray-600">
                                          {t("creditDate")}: {moment(item.summary?.credit_date)?.format("D MMM YYYY")}
                                      </p>
                                  }
                                 </div>
                                {/* {(item?.order?.payment_status !== "paid" && user?.roles[0] === 'Finance')&& */}
                                <Button
                                onClick={() => {
                                    initiatePayment(item?.rfq?.id, item?.summary);
                                }}
                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material">
                                
                                {t("initiatePayment")}
                                </Button>
                                {/* } */}
                              </div>
                              <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold">
                                {/* Initiate Payment Button */}
                                {item.payments?.length || 0} {t("records")}
                              </span>
                            </div>
                            <table className="min-w-full ">
                              <thead className="bg-gray-50/50 border-b border-gray-100">
                                <tr className="text-[11px] text-gray-400 uppercase font-semibold">
                                  <th className="px-6 py-2 text-center">{t("date")}</th>
                                  <th className="px-6 py-2 text-center">{t("mode")}</th>
                                  <th className="px-6 py-2 text-center">{t("amount")}</th>
                                  <th className="px-6 py-2 text-center">{t("status")}</th>
                                  <th className="px-6 py-2 text-center">{t("receipt")}</th>
                                  <th className="px-6 py-2 text-center">{t("taxInvoice")}</th>
                                  <th className="px-6 py-2 text-center"></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y text-sm">
                                {item.payments.map((pay) => (
                                  <tr key={pay.id} className="hover:bg-gray-50/80 transition-colors">
                                    <td className="px-6 py-3 text-gray-500 text-center">
                                      {new Date(pay.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-3 text-gray-700 italic text-center">
                                      {pay.payment_mode || "N/A"}
                                    </td>
                                    <td className="px-6 py-3 text-center font-semibold text-gray-900">
                                      SAR {pay.amount_paid || "0.00"}
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${pay.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {/* {pay.status.toUpperCase()} */}
                                        {t(pay.status?.toLowerCase(), { defaultValue: pay.status })}
                                      </span>
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                      <div className="flex justify-center gap-2">
                                        {pay?.payment_invoice && <DownloadFile fileUrl={pay?.payment_invoice} label={t("invoice")}/>}
                                        {pay?.payment_receipt?.map((url, i) => (
                                        <span key={i} className="flex items-center gap-2">
                                              <button
                                                  type="button"
                                                  title={t("customerPaymentReceipt")}
                                                  onClick={() => setDocumentUrl(url)}
                                                  className="p-1.5 rounded-md hover:bg-gray-200 text-gray-700 hover:text-blue-600 transition"
                                              >
                                                  <EyeIcon title={t("customerPaymentReceipt")} className="h-4 w-4" />
                                              </button>
                                          </span>
                                        ))}
                                      </div>
                                    </td>
                                    <td className="px-6 py-3 text-center">
                                      <div className="flex justify-center gap-2 items-center">
                                        {pay?.uploaded_tax_invoice ? (
                                          <>
                                            <button
                                              type="button"
                                              title={t("viewTaxInvoice")}
                                              onClick={() => setDocumentUrl(pay.uploaded_tax_invoice)}
                                              className="p-1.5 rounded-md hover:bg-gray-200 text-gray-700 hover:text-blue-600 transition"
                                            >
                                              <EyeIcon className="h-4 w-4" />
                                            </button>
                                            <DownloadFile fileUrl={pay.uploaded_tax_invoice} label={t("taxInvoice")} />
                                          </>
                                        ) : null}

                                        {pay.status === 'approved' && (
                                          <label className="p-1.5 rounded-md hover:bg-gray-200 text-gray-700 hover:text-green-600 transition cursor-pointer">
                                            <ArrowUpTrayIcon 
                                              className="h-4 w-4"
                                              title={pay?.uploaded_tax_invoice ? t("reUploadTaxInvoice") : t("uploadTaxInvoice")} 
                                            />
                                            <input
                                              type="file"
                                              accept=".pdf,image/*"
                                              className="hidden"
                                              onChange={(e) => handleTaxInvoiceUpload(e, pay.id)}
                                            />
                                          </label>
                                        )}
                                      </div>
                                    </td>
                                    {/* Actions */}
                                    <td className="px-6 py-3 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            {pay.status === 'pending' && (user?.roles.some(r => ['Finance', 'Administrator'].includes(r))) && (
                                                <Popover
                                                    isOpen={actionPopoverOpen[pay.id]}
                                                    positions={["bottom", "left"]}
                                                    padding={4}
                                                    onClickOutside={() => toggleActionPopover(pay.id)}
                                                    content={
                                                        <div className="bg-white shadow-xl rounded-md w-32 flex flex-col z-50 border border-gray-200 py-1 overflow-hidden">
                                                            <button 
                                                                className="text-left text-sm text-gray-700 hover:bg-gray-50 px-4 py-2 transition-colors" 
                                                                onClick={() => onApproveReject('approved', pay.id, item.summary)}
                                                            >
                                                                {t("approve")}
                                                            </button>
                                                            <button 
                                                                className="text-left text-sm text-red-600 hover:bg-red-50 px-4 py-2 transition-colors" 
                                                                onClick={() => onApproveReject('rejected', pay.id, item.summary)}
                                                            >
                                                                {t("reject")}
                                                            </button>
                                                        </div>
                                                    }
                                                >
                                                    <IconButton
                                                        variant="text"
                                                        size="sm"
                                                        className="rounded-full hover:bg-gray-100"
                                                        onClick={() => toggleActionPopover(pay.id)}
                                                    >
                                                        <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
                                                    </IconButton>
                                                </Popover>
                                            )}
                                        </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
         
          </tbody>
        </table>
        {/* Loading / End of list Indicators */}
        <div className="p-4 text-center">
          {/* {list_loading && <div className="text-blue-600 font-medium animate-pulse">Loading more data...</div>} */}
          {(!list_loading && page === 1 && payment_list?.paymentlist?.data?.length === 0) && (
            <div className="text-gray-400 text-sm">{t("noDataFound")}</div>
          )}
        </div>
        </div>
        </div>
       {documentUrl && (
            <DocumentViewModal documentUrl={documentUrl} setIsDocumentViewModalOpen={setDocumentUrl} />
        )}
    </div>
  );
}

/* ---------- Reusable Components ---------- */

function StatCard({ title, value }) {
  return (
    <div className="bg-white p-4 rounded-xl shadow-sm">
      <p className="text-lg font-semibold">
        {value.toLocaleString()}
      </p>
      <p className="text-sm text-gray-500">{title}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    approved: "bg-green-100 text-green-700 border-green-200",
    rejected: "bg-red-100 text-red-700 border-red-200",
    pending: "bg-red-100 text-red-700 border-red-200",
    paid: "bg-green-100 text-green-700 border-green-200",
    partial: "bg-orange-100 text-orange-700 border-orange-200",
  };

  return (
    <span className={`px-3 py-1 rounded-lg text-xs font-bold border ${styles[status] || "bg-gray-100 text-gray-700"}`}>
      {status.replace('_', ' ').toUpperCase()}
    </span>
  );
}